import * as yup from "yup";
import { useForm } from "react-hook-form";
import { showAlert } from "../Alert";
import { yupResolver } from "@hookform/resolvers/yup";
import { ChangeEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ModalProps } from "../../molecules/Modals";
import { ModalTechDoc } from "../../molecules/Modals/ModalTechDoc";
import { request } from "../../../common/api";
import { SelectOption } from "../../../common/types/selectOption";
import {
  GET_MACHINERY_SELECT_OPTIONS,
  GET_TECH_DOC,
  GET_TECH_DOC_SELECT_OPTIONS,
} from "../../../common/query-keys";
import { CurrentShip } from "../../../common/states/ship";

interface TechDocUploadsModalProps extends ModalProps {
  setModalShow: () => void;
  currentShipInfo: CurrentShip;
}

const schema = yup.object({
  dcmTypeName: yup.string().nullable().required("문서 유형을 선택해주세요."),
  dcmTitle: yup.string().nullable().required("문서 제목을 입력하세요."),
  dcmMcyName: yup.string(),
});

export const TechDocUploadModal = ({
  formId,
  setModalShow,
  currentShipInfo,
}: TechDocUploadsModalProps) => {
  const queryClient = useQueryClient();

  const { setValue, handleSubmit, reset, control } = useForm({
    resolver: yupResolver(schema),
  });

  const [select, setSelect] = useState<{
    dcmTitle?: string;
    dcmMcyName?: string;
    dcmTypeName?: string;
  }>({
    dcmTitle: "",
    dcmMcyName: "",
    dcmTypeName: "",
  });

  /** 기술문서 파일 등록 API */
  const { mutateAsync: postTechDocData } = useMutation({
    mutationFn: (data: FormData) =>
      request({
        method: "POST",
        url: "/tech-doc",
        data: data,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    onSuccess: () => {
      setModalShow();
      reset({
        dcmTitle: "",
        dcmMcyName: "",
        dcmTypeName: "",
      });
      showAlert("파일이 등록되었습니다.");
      queryClient.invalidateQueries({ queryKey: [GET_TECH_DOC] });
    },
    onError: (error) => {
      console.log("error", error);
    },
  });

  const onSubmit = async (data: any) => {
    const files = data.file;
    data.dcmMcyName = select.dcmMcyName;
    data.dcmTypeName = select.dcmTypeName;
    data.dcmShiptypeNo = currentShipInfo.shipTypeNo;
    data.dcmShiptypeName = currentShipInfo.shipTypeName;
    data.dcmShipNo = currentShipInfo.shipNo;
    data.dcmShipName = currentShipInfo.shipName;

    if (files && files.length > 0) {
      // 파일 용량 체크
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) {
          return showAlert("파일 용량이 10MB를 초과하였습니다.");
        }
      }
      const formData = new FormData();
      files.forEach((file: any) => {
        formData.append("files", file);
      });
      formData.append("data", JSON.stringify(data));
      try {
        await postTechDocData(formData);
      } catch (error) {
        console.log("error", error);
      }
    } else {
      return showAlert("파일을 업로드해주세요.");
    }
  };

  // 문서유형 options 조회
  const { data: documentTypeSelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_TECH_DOC_SELECT_OPTIONS, "all"],
    queryFn: async () => {
      return await request<SelectOption[]>({
        method: "GET",
        url: "/select/options/techDoc",
      });
    },
  });

  // 장비 options 조회
  const { data: machinerySelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_MACHINERY_SELECT_OPTIONS, currentShipInfo.shipNo],
    queryFn: async () => {
      if (!currentShipInfo.shipNo) {
        return [];
      }

      return await request<SelectOption[]>({
        method: "GET",
        url: `/equipment-info/machinery/select/options/${currentShipInfo.shipNo}`,
      });
    },
  });

  // 문서유형 선택 변경
  const handleTypeSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelect({ ...select, dcmTypeName: e.target.value });
    setValue("dcmTypeName", e.target.value);
  };

  // 장비 선택 변경
  const handleMcySelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelect({ ...select, dcmMcyName: e.target.value });
    setValue("dcmMcyName", e.target.options[e.target.selectedIndex].text);
  };

  return (
    <ModalTechDoc
      select={select}
      formId={formId}
      control={control}
      onSubmit={handleSubmit(onSubmit)}
      setModalShow={setModalShow}
      handleMcySelectChange={handleMcySelectChange}
      machinerySelectOptions={machinerySelectOptions}
      handleTypeSelectChange={handleTypeSelectChange}
      documentTypeSelectOptions={documentTypeSelectOptions}
    />
  );
};
